import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import type { CarouselThumbnailConfig } from '@packages/shared';
import { ErrorBoundary, DynamicYield, Box, Carousel, Image, useDeviceType } from '@packages/shared';
import { Enlarge32 } from '@packages/themes/icons';
import { useConfig } from '@packages/utilities';
import type { ReactNode } from 'react';
import { Suspense, forwardRef, lazy, useRef, useState } from 'react';
import { useResponsiveAkamaiTemplate } from '../../hooks/useResponsiveAkamaiTemplate';
import { Cliplister } from '../Cliplister';
import { ImageGalleryCallToActionButton } from '../ImageGalleryCallToActionButton';
import { ImageGalleryFlags } from './ImageGalleryFlags/ImageGalleryFlags';
import { ImageGalleryThumbnail } from './ImageGalleryThumbnail/ImageGalleryThumbnail';
import { ImageGalleryActionsWrapper } from './ImageGalleryActionsWrapper/ImageGalleryActionsWrapper';

// TODO(api-layer): add alt field to media type
const ImageGalleryFragment = graphql(/* GraphQL */ `
  fragment ImageGallery on Product {
    name
    media {
      type
      url
    }
    additionalFlags {
      type
      text
      src
    }
    ...CliplisterFragment
  }
`);

type ImageGalleryProps = {
  productData: FragmentType<typeof ImageGalleryFragment>;
  colorPicker?: ReactNode;
};

const LazyZoomDialog = lazy(
  () =>
    import(
      /* webpackChunkName: 'ImageGalleryZoomDialog' */ './ImageGalleryZoomDialog/ImageGalleryZoomDialog'
    ),
);

/**
 * ImageGallery on detailview
 * */
export const ImageGallery = forwardRef<HTMLDivElement, ImageGalleryProps>(
  ({ productData, colorPicker }, ref) => {
    const product = getFragmentData(ImageGalleryFragment, productData);
    const { media, additionalFlags, name } = product;
    const { isMobile } = useDeviceType();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const parentRef = useRef<HTMLDivElement>(null);
    const [openZoomDialog, setOpenZoomDialog] = useState(false);
    const akamaiTemplate = useResponsiveAkamaiTemplate('gallery');
    const { dynamicYieldEnabled } = useConfig();

    const thumbnailConfig: CarouselThumbnailConfig = {
      visibleSlides: 'auto',
      gap: 1.5,
      thumbnails: media.map(({ url }) => (
        <ImageGalleryThumbnail imgSrc={url!} alt={name} key={url} />
      )),
    };

    return (
      <Box ref={parentRef} sx={{ height: '100%' }}>
        {openZoomDialog && (
          <ErrorBoundary>
            <Suspense>
              <LazyZoomDialog
                open={openZoomDialog}
                index={selectedIndex}
                onChange={(index) => openZoomDialog && setSelectedIndex(index)}
                onClose={() => setOpenZoomDialog(false)}
                productData={product}
              />
            </Suspense>
          </ErrorBoundary>
        )}
        {/* transform: 'scale(1)' overrides the viewport for display: 'fixed' property of underlying ArrowButton component */}
        <Box sx={{ transform: 'scale(1)', width: '100%' }} ref={ref} position="sticky" top={0}>
          <Carousel
            index={selectedIndex}
            showArrows={!isMobile}
            thumbnailConfig={thumbnailConfig}
            onChange={(index) => !openZoomDialog && setSelectedIndex(index)}
          >
            {media.map(({ url }, i) => (
              <Box
                key={url}
                sx={{
                  width: '100%',
                  height: 0,
                  paddingTop: '100%',
                  position: 'relative',
                  cursor: 'pointer',
                }}
              >
                {/* TODO(api-layer): add alt field to media type */}
                <Image
                  loading={i !== 0 ? 'lazy' : undefined}
                  priority={i === 0}
                  src={`${url}?${akamaiTemplate}`}
                  alt={name}
                  style={{ objectFit: 'contain' }}
                  unoptimized
                  fill
                  onClick={() => setOpenZoomDialog(true)}
                  data-testid={i}
                />
              </Box>
            ))}
          </Carousel>
          <ImageGalleryActionsWrapper isMobile={isMobile}>
            <Cliplister productData={product} />
            <ImageGalleryCallToActionButton onClick={() => setOpenZoomDialog(true)}>
              <Enlarge32 sx={{ fill: (theme) => theme.palette.text.dark }} />
            </ImageGalleryCallToActionButton>
            {isMobile && colorPicker}
          </ImageGalleryActionsWrapper>
          {dynamicYieldEnabled && (
            <>
              <DynamicYield dataDyId="dv_social-proof-inject" />
              <DynamicYield dataDyId="dv_social-proof" />
            </>
          )}
          <ImageGalleryFlags flags={additionalFlags} />
        </Box>
      </Box>
    );
  },
);
